// Stok sayımı API istemcisi (api/StockCount, gts_accounting_api).
// İstek gövdeleri PascalCase; yanıtlar camelCase okunur.
import axios from 'axios';
import { accountingApi } from '../../../../config/apiClient';
import { API_BASE_URL } from '../../../../config/api';

const BASE = `${API_BASE_URL}/api/StockCount`;

export type StockCountStatus = 'DRAFT' | 'COUNTING' | 'REVIEW' | 'COMPLETED' | 'CANCELLED';
export type StockCountType = 'FULL' | 'PARTIAL' | 'QUICK';

export interface StockCountScope {
  categoryIds: string[];
  brandIds: string[];
  itemIds: string[];
  includeZeroStock: boolean;
  includeInactive: boolean;
}

export interface StockCountAssignee {
  userUid: string;
  userName?: string | null;
  role?: string | null;
  categoryIds: string[];
  allowManualEntry: boolean;
  status: 'PENDING' | 'COUNTING' | 'SUBMITTED';
  submittedAt?: string | null;
}

export interface StockCountSummary {
  id: string;
  number: string;
  name?: string | null;
  type: StockCountType;
  status: StockCountStatus;
  warehouseId: string;
  warehouseName?: string | null;
  branchId?: string | null;
  isBlind: boolean;
  plannedStartAt?: string | null;
  plannedEndAt?: string | null;
  startedAt?: string | null;
  approvedAt?: string | null;
  lineCount: number;
  countedLineCount: number;
  surplusValue: number;
  shortageValue: number;
  assignees: StockCountAssignee[];
  createdAt: string;
  createdByName?: string | null;
}

export interface StockCountLine {
  id: string;
  itemId: string;
  itemName?: string | null;
  stockCode?: string | null;
  barcode?: string | null;
  unit?: string | null;
  categoryId?: string | null;
  lotId?: string | null;
  lotNo?: string | null;
  expiryDate?: string | null;
  snapshotQty?: number | null;
  countedQty?: number | null;
  isOutOfScope: boolean;
  recountRequested: boolean;
  hasManualEntry: boolean;
  lastCountedAt?: string | null;
}

export interface StockCountDetail extends StockCountSummary {
  notes?: string | null;
  scope: StockCountScope;
  tolerancePercent?: number | null;
  toleranceAmount?: number | null;
  showsExpected: boolean;
  canEnterManually: boolean;
  lines: StockCountLine[];
  submittedAt?: string | null;
  approvalNote?: string | null;
  zeroedUncounted?: boolean;
  adjustedLineCount?: number;
  cancelReason?: string | null;
}

export interface StockCountDifferenceRow {
  countId: string;
  number: string;
  countName?: string | null;
  type: StockCountType;
  warehouseId: string;
  warehouseName?: string | null;
  approvedAt?: string | null;
  itemId: string;
  itemName?: string | null;
  stockCode?: string | null;
  barcode?: string | null;
  unit?: string | null;
  lotNo?: string | null;
  expectedQty?: number | null;
  countedQty?: number | null;
  differenceQty?: number | null;
  unitCost: number;
  differenceValue?: number | null;
  hasManualEntry: boolean;
  isOutOfScope: boolean;
}

export interface StockCountEntry {
  id: string;
  lineId?: string | null;
  itemId?: string | null;
  itemName?: string | null;
  quantity: number;
  entryUnit?: string | null;
  entryQuantity?: number | null;
  method: string;
  barcode?: string | null;
  countedByUid: string;
  countedByName?: string | null;
  isUndone: boolean;
  createdAt: string;
}

export interface AddEntryResult {
  entryId?: string | null;
  lineId?: string | null;
  itemId?: string | null;
  itemName?: string | null;
  unit?: string | null;
  countedQty?: number | null;
  quantity: number;
  packageName?: string | null;
  packageFactor?: number | null;
  isUnknownBarcode: boolean;
  isOutOfScope: boolean;
  duplicate: boolean;
}

export interface ReviewLine {
  id: string;
  itemId: string;
  itemName?: string | null;
  stockCode?: string | null;
  unit?: string | null;
  lotId?: string | null;
  lotNo?: string | null;
  expiryDate?: string | null;
  snapshotQty: number;
  movementQty: number;
  expectedQty: number;
  countedQty?: number | null;
  differenceQty?: number | null;
  unitCost: number;
  differenceValue?: number | null;
  overTolerance: boolean;
  movedDuringCount: boolean;
  isOutOfScope: boolean;
  hasManualEntry: boolean;
  recountRequested: boolean;
  countedBy: string[];
}

export interface StockCountReview {
  count: StockCountSummary;
  zeroUncounted: boolean;
  countedLines: number;
  uncountedLines: number;
  differentLines: number;
  overToleranceLines: number;
  movedDuringCountLines: number;
  surplusValue: number;
  shortageValue: number;
  lines: ReviewLine[];
  unknownBarcodes: { barcode: string; quantity: number; countedBy: string[] }[];
}

export interface SaveStockCountInput {
  name?: string;
  type: StockCountType;
  warehouseId: string;
  scope: StockCountScope;
  isBlind: boolean;
  tolerancePercent?: number | null;
  toleranceAmount?: number | null;
  plannedStartAt?: string | null;
  plannedEndAt?: string | null;
  notes?: string;
  assignees: { userUid: string; userName?: string; role?: string; categoryIds?: string[]; allowManualEntry: boolean }[];
  startNow: boolean;
}

export class StockCountError extends Error {
  code?: string;
  status?: number;
  constructor(message: string, code?: string, status?: number) {
    super(message);
    this.name = 'StockCountError';
    this.code = code;
    this.status = status;
  }
}

const toError = (error: unknown): StockCountError => {
  if (error instanceof StockCountError) return error;
  if (axios.isAxiosError(error)) {
    const body: any = error.response?.data;
    return new StockCountError(body?.message || body?.Message || 'Sayım işleminde bir hata oluştu.', body?.code || body?.Code, error.response?.status);
  }
  return new StockCountError(error instanceof Error ? error.message : 'Sayım işleminde bir hata oluştu.');
};

async function call<T>(fn: () => Promise<{ data: any }>): Promise<T> {
  try {
    const response = await fn();
    const body = response.data;
    if (body?.success === false || body?.Success === false) {
      throw new StockCountError(body?.message || body?.Message || 'İşlem başarısız.', body?.code || body?.Code);
    }
    return (body?.data ?? body?.Data) as T;
  } catch (error) {
    throw toError(error);
  }
}

const pascalScope = (s: StockCountScope) => ({
  CategoryIds: s.categoryIds,
  BrandIds: s.brandIds,
  ItemIds: s.itemIds,
  IncludeZeroStock: s.includeZeroStock,
  IncludeInactive: s.includeInactive,
});

const pascalSave = (input: SaveStockCountInput) => ({
  Name: input.name || null,
  Type: input.type,
  WarehouseId: input.warehouseId,
  Scope: pascalScope(input.scope),
  IsBlind: input.isBlind,
  TolerancePercent: input.tolerancePercent ?? null,
  ToleranceAmount: input.toleranceAmount ?? null,
  PlannedStartAt: input.plannedStartAt || null,
  PlannedEndAt: input.plannedEndAt || null,
  Notes: input.notes || null,
  Assignees: input.assignees.map((a) => ({
    UserUid: a.userUid,
    UserName: a.userName || null,
    Role: a.role || 'personnel',
    CategoryIds: a.categoryIds ?? [],
    AllowManualEntry: a.allowManualEntry,
  })),
  StartNow: input.startNow,
});

export const newRequestId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

export const stockCountService = {
  async list(params: { status?: string; warehouseId?: string; search?: string; page?: number; pageSize?: number } = {}) {
    try {
      const response = await accountingApi.get(BASE, { params });
      const body: any = response.data;
      return { items: (body?.data ?? []) as StockCountSummary[], totalCount: Number(body?.totalCount ?? 0) };
    } catch (error) {
      throw toError(error);
    }
  },
  myAssignments: () => call<any[]>(() => accountingApi.get(`${BASE}/my-assignments`)),
  get: (id: string) => call<StockCountDetail>(() => accountingApi.get(`${BASE}/${id}`)),
  create: (input: SaveStockCountInput) => call<StockCountDetail>(() => accountingApi.post(BASE, pascalSave(input))),
  update: (id: string, input: SaveStockCountInput) => call<StockCountDetail>(() => accountingApi.post(`${BASE}/${id}/update`, pascalSave(input))),
  start: (id: string) => call<StockCountDetail>(() => accountingApi.post(`${BASE}/${id}/start`, {})),
  addEntry: (id: string, input: {
    itemId?: string; barcode?: string; lotId?: string; lotNo?: string; expiryDate?: string;
    quantity: number; entryUnit?: string; entryFactor?: number; entryQuantity?: number; method: string;
  }) => call<AddEntryResult>(() => accountingApi.post(`${BASE}/${id}/entries`, {
    ItemId: input.itemId ?? null,
    Barcode: input.barcode ?? null,
    LotId: input.lotId ?? null,
    LotNo: input.lotNo ?? null,
    ExpiryDate: input.expiryDate ?? null,
    Quantity: input.quantity,
    EntryUnit: input.entryUnit ?? null,
    EntryFactor: input.entryFactor ?? null,
    EntryQuantity: input.entryQuantity ?? null,
    Method: input.method,
    ClientRequestId: newRequestId(),
  })),
  entries: (id: string, params: { lineId?: string; mine?: boolean; take?: number } = {}) =>
    call<StockCountEntry[]>(() => accountingApi.get(`${BASE}/${id}/entries`, { params })),
  undo: (id: string, entryId: string) => call<AddEntryResult>(() => accountingApi.post(`${BASE}/${id}/entries/${entryId}/undo`, {})),
  setTotal: (id: string, lineId: string, quantity: number) =>
    call<AddEntryResult>(() => accountingApi.post(`${BASE}/${id}/set-total`, { LineId: lineId, Quantity: quantity, ClientRequestId: newRequestId() })),
  submit: (id: string) => call<StockCountSummary>(() => accountingApi.post(`${BASE}/${id}/submit`, {})),
  review: (id: string, zeroUncounted = false) =>
    call<StockCountReview>(() => accountingApi.get(`${BASE}/${id}/review`, { params: { zeroUncounted } })),
  recount: (id: string, lineIds: string[]) => call<StockCountSummary>(() => accountingApi.post(`${BASE}/${id}/recount`, { LineIds: lineIds })),
  approve: (id: string, zeroUncounted: boolean, note?: string) =>
    call<StockCountReview>(() => accountingApi.post(`${BASE}/${id}/approve`, { ZeroUncounted: zeroUncounted, Note: note || null })),
  differences: (params: { from?: string; to?: string; warehouseId?: string; search?: string; includeZero?: boolean } = {}) =>
    call<StockCountDifferenceRow[]>(() => accountingApi.get(`${BASE}/differences`, { params })),
  cancel: (id: string, reason?: string) => call<StockCountSummary>(() => accountingApi.post(`${BASE}/${id}/cancel`, { Reason: reason || null })),
};

export const STATUS_LABEL: Record<StockCountStatus, string> = {
  DRAFT: 'Taslak',
  COUNTING: 'Sayılıyor',
  REVIEW: 'İncelemede',
  COMPLETED: 'Tamamlandı',
  CANCELLED: 'İptal',
};
export const STATUS_COLOR: Record<StockCountStatus, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  DRAFT: 'default',
  COUNTING: 'info',
  REVIEW: 'warning',
  COMPLETED: 'success',
  CANCELLED: 'error',
};
export const TYPE_LABEL: Record<StockCountType, string> = { FULL: 'Tam', PARTIAL: 'Kısmi', QUICK: 'Hızlı' };

export const fmtQty = (n?: number | null) =>
  n == null ? '—' : n.toLocaleString('tr-TR', { maximumFractionDigits: 3 });
export const fmtMoney = (n?: number | null) =>
  n == null ? '—' : n.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' });
export const fmtDate = (s?: string | null) => {
  if (!s) return '—';
  const d = new Date(s.endsWith('Z') || s.includes('+') ? s : `${s}Z`);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' });
};
